import { useLayoutEffect } from "react";

function setMeta(name, content, prop = false) {
  if (content == null) return;
  const sel = `meta[${prop ? "property" : "name"}="${name}"]`;
  let el = document.head.querySelector(sel);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(prop ? "property" : "name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function removeMeta(name, prop = false) {
  const sel = `meta[${prop ? "property" : "name"}="${name}"]`;
  document.head.querySelector(sel)?.remove();
}

const CANONICAL_ORIGIN = new URL(process.env.REACT_APP_CANONICAL_ORIGIN).origin;
const CANONICAL_HOST = new URL(CANONICAL_ORIGIN).hostname;
const OG_LOCALE = { EN: "en_AE", TR: "tr_TR", RU: "ru_RU" };
const HTML_LANG = { EN: "en", TR: "tr", RU: "ru" };
const HREFLANG = { EN: "en", TR: "tr", RU: "ru" };
const SERP_TITLE_LIMIT_PX = { EN: 580, TR: 580, RU: 600 };
const BRAND_SUFFIX = " | Silver Drive";
const DEFAULT_OG_IMAGE = `${CANONICAL_ORIGIN}/og/silver-drive-og-1200x630.jpg`;
const DEFAULT_OG_IMAGE_ALT = "Silver Drive luxury car rental in Dubai";

const absoluteUrl = (path) => new URL(path || DEFAULT_OG_IMAGE, CANONICAL_ORIGIN).href;

const titleCharacterWidth = (character) => {
  if (/[ilI.,'|!]/.test(character)) {
    return 4;
  }
  if (/[mwMW@#%&]/.test(character)) {
    return 13;
  }
  if (character === " ") {
    return 5;
  }
  if (/[A-Z0-9]/.test(character)) {
    return 10;
  }
  if (/[\u0400-\u04FF]/.test(character)) {
    return 9;
  }
  return 8;
};

export const estimatedSerpTitleWidth = (title = "") => (
  [...title].reduce((total, character) => total + titleCharacterWidth(character), 0)
);

export const fitSerpTitle = (title = "", language = "EN") => {
  const primary = title
    .replace(/\s*\|\s*Silver Drive Rent a Car\s*$/i, "")
    .replace(/\s*\|\s*Silver Drive\s*$/i, "")
    .trim();
  const suffixWidth = estimatedSerpTitleWidth(BRAND_SUFFIX);
  const titleBudget = (SERP_TITLE_LIMIT_PX[language] || 580) - suffixWidth;

  if (estimatedSerpTitleWidth(primary) <= titleBudget) {
    return `${primary}${BRAND_SUFFIX}`;
  }

  const words = primary.split(/\s+/);
  const fitted = [];
  for (const word of words) {
    const candidate = [...fitted, word].join(" ");
    if (estimatedSerpTitleWidth(candidate) > titleBudget - 10) {
      break;
    }
    fitted.push(word);
  }
  const compactPrimary = fitted.join(" ") || primary.slice(0, 48).trim();
  return `${compactPrimary}…${BRAND_SUFFIX}`;
};

export const fitEnglishSerpTitle = (title = "") => fitSerpTitle(title, "EN");

export function useSeo({
  title,
  description,
  keywords,
  canonical,
  image,
  jsonLd,
  alternates,
  noIndex = false,
}) {
  const lang = currentLang();
  const resolvedTitle = fitSerpTitle(title, lang);
  const resolvedOgImage = absoluteUrl(image || DEFAULT_OG_IMAGE);
  const usesDefaultOgImage = !image;
  const jsonLdString = jsonLd ? JSON.stringify(jsonLd) : "";
  const alternatesString = JSON.stringify(alternates || []);

  useLayoutEffect(() => {
    if (typeof document !== "undefined") document.documentElement.lang = HTML_LANG[lang] || "en";
    if (resolvedTitle) document.title = resolvedTitle;
    setMeta("description", description);
    const currentHost = typeof window === "undefined" ? CANONICAL_HOST : window.location.hostname;
    const shouldNoIndex = noIndex || currentHost !== CANONICAL_HOST;
    setMeta("robots", shouldNoIndex ? "noindex, nofollow" : "index, follow");
    if (keywords) setMeta("keywords", keywords);
    setMeta("og:locale", OG_LOCALE[lang] || "en_AE", true);
    setMeta("og:title", resolvedTitle, true);
    setMeta("og:description", description, true);
    setMeta("og:type", "website", true);
    if (canonical) setMeta("og:url", canonical, true);
    setMeta("og:image", resolvedOgImage, true);
    setMeta("og:image:secure_url", resolvedOgImage, true);
    setMeta("og:image:type", "image/jpeg", true);
    setMeta("og:image:alt", usesDefaultOgImage ? DEFAULT_OG_IMAGE_ALT : resolvedTitle, true);
    if (usesDefaultOgImage) {
      setMeta("og:image:width", "1200", true);
      setMeta("og:image:height", "630", true);
    } else {
      removeMeta("og:image:width", true);
      removeMeta("og:image:height", true);
    }
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", resolvedTitle);
    setMeta("twitter:description", description);
    setMeta("twitter:image", resolvedOgImage);

    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    if (canonical) link.setAttribute("href", canonical);

    document.querySelectorAll('link[data-hreflang="1"]').forEach((e) => e.remove());
    const alternateItems = JSON.parse(alternatesString);
    if (Array.isArray(alternateItems)) {
      for (const alt of alternateItems) {
        const l = document.createElement("link");
        l.setAttribute("rel", "alternate");
        l.setAttribute("hreflang", alt.lang);
        l.setAttribute("href", alt.url);
        l.setAttribute("data-hreflang", "1");
        document.head.appendChild(l);
      }
    }

    let script = document.getElementById("ld-json");
    if (jsonLdString) {
      if (!script) {
        script = document.createElement("script");
        script.type = "application/ld+json";
        script.id = "ld-json";
        document.head.appendChild(script);
      }
      script.textContent = jsonLdString;
    } else if (script) {
      script.remove();
    }
  }, [
    resolvedTitle,
    description,
    keywords,
    canonical,
    image,
    resolvedOgImage,
    usesDefaultOgImage,
    jsonLdString,
    alternatesString,
    noIndex,
    lang,
  ]);
}

export const fillTpl = (tpl, map) => Object.entries(map || {}).reduce((s, [k, v]) => s.split(`{${k}}`).join(v ?? ""), tpl || "");

export const ORIGIN = CANONICAL_ORIGIN;

export const localizedAlternates = (paths) => {
  const alternates = Object.entries(HREFLANG)
    .filter(([code]) => paths?.[code])
    .map(([code, hreflang]) => ({
      lang: hreflang,
      url: `${ORIGIN}${paths[code]}`,
    }));

  if (paths?.EN) {
    alternates.push({ lang: "x-default", url: `${ORIGIN}${paths.EN}` });
  }

  return alternates;
};

const BRAND_KEY_ALIASES = {
  "land-rover": "range-rover",
  "range-rover": "range-rover",
};

export const canonicalBrandKey = (key = "") => {
  const normalized = String(key)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  return BRAND_KEY_ALIASES[normalized] || normalized;
};

export const canonicalModelRouteSlug = (slug = "") => (
  String(slug).replace(/land-rover/gi, "range-rover")
);

export const modelSlug = (brand, model) =>
  `${canonicalBrandKey(brand)} ${model}`
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

export const LANG_PREFIX = { EN: "/eng", TR: "/tr", RU: "/ru" };
export const langFromPath = (p) => (p === "/ru" || p.startsWith("/ru/")) ? "RU" : (p === "/tr" || p.startsWith("/tr/")) ? "TR" : "EN";
export const stripLang = (p) => { const s = (p || "/").replace(/^\/(ru|tr|eng)(?=\/|$)/, ""); return s || "/"; };
export const currentLang = () => langFromPath(typeof window !== "undefined" ? window.location.pathname : "/");
export const withLang = (path, lang = currentLang()) => { const pre = LANG_PREFIX[lang] || ""; return `${pre}${path === "/" ? "" : path}` || "/"; };
export const lp = (path) => withLang(path);

const B_SUFFIX = { EN: "-rental-dubai", TR: "-kiralama-dubai" };
export const brandSlugFor = (key, lang = currentLang()) => {
  const canonicalKey = canonicalBrandKey(key);

  return lang === "RU"
    ? `аренда-${canonicalKey}-дубай`
    : `${canonicalKey}${B_SUFFIX[lang] || B_SUFFIX.EN}`;
};
export const brandKeyFromSlug = (slug) => canonicalBrandKey(
  (slug || "")
    .replace(/^аренда-/, "")
    .replace(/-(rental|kiralama)-dubai$/, "")
    .replace(/-дубай$/, ""),
);
export const brandPath = (key) => { const l = currentLang(); return withLang(`/rent/${brandSlugFor(key, l)}`, l); };

export const modelSlugFor = (brand, model, lang = currentLang()) => { const c = modelSlug(brand, model); return lang === "RU" ? `аренда-${c}-дубай` : `${c}${B_SUFFIX[lang] || B_SUFFIX.EN}`; };
export const modelCoreFromSlug = (slug) => (slug || "").replace(/^аренда-/, "").replace(/-(rental|kiralama)-dubai$/, "").replace(/-дубай$/, "");
export const modelPath = (brand, model) => { const l = currentLang(); return withLang(`/${modelSlugFor(brand, model, l)}`, l); };

const carDetailModelTokens = (car) => {
  const modelAndTrim = [car?.model, car?.trim].filter(Boolean).join(" ");
  return canonicalBrandKey(modelAndTrim).split("-").filter(Boolean);
};

export const carDetailRouteKey = (car) => [
  canonicalBrandKey(car?.brand),
  ...carDetailModelTokens(car),
].filter(Boolean).join("/");

export const carDetailRouteKeyFromParams = (brand, model, tail = "") => {
  const modelAndVersion = [model, ...String(tail).split("/")].filter(Boolean).join(" ");
  const modelTokens = canonicalBrandKey(modelAndVersion).split("-").filter(Boolean);
  return [canonicalBrandKey(brand), ...modelTokens].filter(Boolean).join("/");
};

export const carDetailPath = (car, lang = currentLang()) => (
  withLang(`/rent/${carDetailRouteKey(car)}`, lang)
);
